import { Alert, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useMutation } from '../../hooks/useMutation';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageHeader } from '../../shared/layout/PageHeader';
import { MutationError } from '../partners/PartnerFormDialog';
import { CalculationPreview } from './CalculationPreview';
import { commissionRulesRepositoryToken } from './repository';
import { bangkokWallTimeToRfc3339 } from './types';
import type { CommissionRulePreview, CommissionRulePreviewInput } from './types';

interface WizardValues {
  readonly scope: 'GLOBAL' | 'PARTNER';
  readonly partnerId: string;
  readonly gameId: string;
  readonly activationMode: 'IMMEDIATE' | 'SCHEDULED';
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
  readonly agentRatePercent: string;
  readonly affiliateRatePercent: string;
  readonly sampleActualPlayAmount: string;
  readonly sampleCurrencyCode: string;
  readonly reason: string;
}

interface PreviewState {
  readonly fingerprint: string;
  readonly value: CommissionRulePreview;
}

const defaults: WizardValues = {
  scope: 'GLOBAL',
  partnerId: '',
  gameId: '',
  activationMode: 'SCHEDULED',
  effectiveFrom: '',
  effectiveTo: '',
  agentRatePercent: '',
  affiliateRatePercent: '',
  sampleActualPlayAmount: '',
  sampleCurrencyCode: 'THB',
  reason: '',
};

export function CommissionRuleWizard({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(commissionRulesRepositoryToken);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const previewMutation = useMutation((input: CommissionRulePreviewInput) =>
    repository.preview(input),
  );
  const createMutation = useMutation(
    ({
      input,
      idempotencyKey,
    }: {
      readonly input: Parameters<typeof repository.create>[0];
      readonly idempotencyKey: string;
    }) => repository.create(input, idempotencyKey),
  );
  const {
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<WizardValues>({ defaultValues: defaults });
  const scope = watch('scope');
  const activationMode = watch('activationMode');
  const error =
    previewMutation.error !== null
      ? toSafeError(previewMutation.error, t('commissionRules.errors.preview'), t)
      : createMutation.error !== null
        ? toSafeError(createMutation.error, t('commissionRules.errors.create'), t)
        : null;

  const preview = handleSubmit(async (values) => {
    try {
      createMutation.reset();
      const value = await previewMutation.mutate(toPreviewInput(values));
      if (value === undefined) return;
      setPreviewState({ fingerprint: fingerprint(values), value });
    } catch {
      // The shared mutation state is rendered through the safe error mapper.
    }
  });

  const create = async () => {
    const values = getValues();
    if (
      previewState === null ||
      previewState.value.blockers.length > 0 ||
      previewState.fingerprint !== fingerprint(values)
    )
      return;
    try {
      const input = toPreviewInput(values);
      previewMutation.reset();
      const result = await createMutation.mutate({
        input: {
          scope: input.scope,
          ...(input.scope === 'PARTNER' ? { partnerId: input.partnerId } : {}),
          gameId: input.gameId,
          activationMode: input.activationMode,
          effectiveFrom: input.effectiveFrom ?? null,
          effectiveTo: input.effectiveTo ?? null,
          agentRatePercent: input.agentRatePercent,
          affiliateRatePercent: input.affiliateRatePercent,
          reason: values.reason.trim(),
          previewHash: previewState.value.previewHash,
        },
        idempotencyKey: idempotencyKeyRef.current,
      });
      if (result === undefined) return;
      idempotencyKeyRef.current = crypto.randomUUID();
      void navigate(`/commission-rules/${result.data.id}`);
    } catch {
      // The shared mutation state is rendered through the safe error mapper.
    }
  };

  const resetForm = () => {
    reset(defaults);
    setPreviewState(null);
    previewMutation.reset();
    createMutation.reset();
    idempotencyKeyRef.current = crypto.randomUUID();
  };
  const currentFingerprint = fingerprint(getValues());
  const previewFresh = previewState !== null && previewState.fingerprint === currentFingerprint;
  const previewing = previewMutation.status === 'submitting';
  const creating = createMutation.status === 'submitting';

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('commissionRules.wizard.subtitle')}
        title={t('commissionRules.wizard.title')}
      />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          component="form"
          noValidate
          onChange={() => {
            setPreviewState(null);
            previewMutation.reset();
            createMutation.reset();
          }}
          onSubmit={preview}
          spacing={2}
        >
          {error === null ? null : <MutationError error={error} />}
          <TextField
            defaultValue="GLOBAL"
            label={t('commissionRules.fields.scope')}
            select
            {...register('scope')}
          >
            <MenuItem value="GLOBAL">GLOBAL</MenuItem>
            <MenuItem value="PARTNER">PARTNER</MenuItem>
          </TextField>
          {scope === 'PARTNER' ? (
            <TextField
              error={errors.partnerId !== undefined}
              helperText={errors.partnerId?.message}
              label={t('commissionRules.fields.partnerId')}
              {...register('partnerId', {
                validate: (value) =>
                  scope !== 'PARTNER' ||
                  value.trim().length > 0 ||
                  t('commissionRules.validation.required'),
              })}
            />
          ) : null}
          <TextField
            error={errors.gameId !== undefined}
            helperText={errors.gameId?.message}
            label={t('commissionRules.fields.gameId')}
            {...register('gameId', { required: t('commissionRules.validation.required') })}
          />
          <TextField
            defaultValue="SCHEDULED"
            label={t('commissionRules.fields.activationMode')}
            select
            {...register('activationMode')}
          >
            <MenuItem value="IMMEDIATE">IMMEDIATE</MenuItem>
            <MenuItem value="SCHEDULED">SCHEDULED</MenuItem>
          </TextField>
          <TextField
            error={errors.effectiveFrom !== undefined}
            helperText={errors.effectiveFrom?.message}
            label={t('commissionRules.fields.effectiveFrom')}
            slotProps={{ inputLabel: { shrink: true } }}
            type="datetime-local"
            {...register('effectiveFrom', {
              validate: (value) =>
                activationMode !== 'SCHEDULED' ||
                value.length > 0 ||
                t('commissionRules.validation.required'),
            })}
          />
          <TextField
            label={t('commissionRules.fields.effectiveTo')}
            slotProps={{ inputLabel: { shrink: true } }}
            type="datetime-local"
            {...register('effectiveTo')}
          />
          <TextField
            error={errors.agentRatePercent !== undefined}
            helperText={errors.agentRatePercent?.message}
            label={t('commissionRules.fields.agentRatePercent')}
            {...register('agentRatePercent', rateRules(t))}
          />
          <TextField
            error={errors.affiliateRatePercent !== undefined}
            helperText={errors.affiliateRatePercent?.message}
            label={t('commissionRules.fields.affiliateRatePercent')}
            {...register('affiliateRatePercent', rateRules(t))}
          />
          <TextField
            error={errors.sampleActualPlayAmount !== undefined}
            helperText={errors.sampleActualPlayAmount?.message}
            label={t('commissionRules.fields.sampleActualPlayAmount')}
            {...register('sampleActualPlayAmount', {
              required: t('commissionRules.validation.required'),
              pattern: {
                value: /^(?!0(?:\.0{1,6})?$)(?:0|[1-9][0-9]{0,13})(?:\.[0-9]{1,6})?$/u,
                message: t('commissionRules.validation.money'),
              },
            })}
          />
          <TextField
            error={errors.sampleCurrencyCode !== undefined}
            helperText={errors.sampleCurrencyCode?.message}
            label={t('commissionRules.fields.sampleCurrencyCode')}
            {...register('sampleCurrencyCode', {
              required: t('commissionRules.validation.required'),
              pattern: { value: /^[A-Z]{3}$/u, message: t('commissionRules.validation.currency') },
            })}
          />
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('commissionRules.fields.reason')}
            minRows={2}
            multiline
            {...register('reason', {
              maxLength: { value: 2000, message: t('commissionRules.validation.reasonMaximum') },
              validate: (value) =>
                value.trim().length > 0 || t('commissionRules.validation.required'),
            })}
          />
          <Alert severity="warning">{t('commissionRules.wizard.impact')}</Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button loading={previewing} type="submit" variant="outlined">
              {t('commissionRules.actions.preview')}
            </Button>
            <Button
              disabled={!previewFresh || previewState.value.blockers.length > 0 || creating}
              loading={creating}
              onClick={() => void create()}
              variant="contained"
            >
              {t('commissionRules.actions.create')}
            </Button>
            <Button disabled={previewing || creating} onClick={resetForm}>
              {t('commissionRules.actions.reset')}
            </Button>
          </Stack>
          {!previewFresh && previewState !== null ? (
            <Typography color="warning.main">{t('commissionRules.preview.stale')}</Typography>
          ) : null}
        </Stack>
      </Paper>
      {previewFresh ? <CalculationPreview preview={previewState.value} /> : null}
    </Stack>
  );
}

function toPreviewInput(values: WizardValues): CommissionRulePreviewInput {
  return {
    scope: values.scope,
    ...(values.scope === 'PARTNER' ? { partnerId: values.partnerId.trim() } : {}),
    gameId: values.gameId.trim(),
    activationMode: values.activationMode,
    effectiveFrom:
      values.effectiveFrom.length === 0 ? null : bangkokWallTimeToRfc3339(values.effectiveFrom),
    effectiveTo:
      values.effectiveTo.length === 0 ? null : bangkokWallTimeToRfc3339(values.effectiveTo),
    agentRatePercent: values.agentRatePercent.trim(),
    affiliateRatePercent: values.affiliateRatePercent.trim(),
    sampleActualPlayAmount: values.sampleActualPlayAmount.trim(),
    sampleCurrencyCode: values.sampleCurrencyCode.trim(),
  };
}

function fingerprint(values: WizardValues): string {
  return JSON.stringify(values);
}

function rateRules(t: (key: string) => string) {
  return {
    required: t('commissionRules.validation.required'),
    pattern: {
      value: /^(?:(?:0|[1-9][0-9]?)(?:\.[0-9]{1,6})?|100(?:\.0{1,6})?)$/u,
      message: t('commissionRules.validation.rate'),
    },
  };
}

export function toSafeError(
  error: unknown,
  fallback: string,
  t: (key: string) => string,
): Pick<AppError, 'message' | 'requestId'> {
  if (typeof error !== 'object' || error === null) return { message: fallback, requestId: null };
  const candidate = error as { readonly code?: unknown; readonly requestId?: unknown };
  const code = typeof candidate.code === 'string' ? candidate.code : '';
  const known: Readonly<Record<string, string>> = {
    EFFECTIVE_PERIOD_OVERLAP: 'commissionRules.notices.overlap',
    PREVIEW_STALE: 'commissionRules.errors.stalePreview',
    INVALID_RULE_LIFECYCLE: 'commissionRules.errors.lifecycle',
    NOT_FOUND: 'commissionRules.errors.notFound',
  };
  return {
    message: known[code] === undefined ? fallback : t(known[code]),
    requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
  };
}
